// @flow
import * as React from 'react';
import { Box, Button } from 'grommet';
import type { Map } from 'esm-web-pro/containers/map/types';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import CableWithFreeFibersSelect
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/CableWithFreeFibersSelect';
import FiberSelect from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/FiberSelect';
import RangeSelect from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/RangeSelect';
import { injectIntl, IntlShape } from 'react-intl';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import type { ODF as ODFType, ODFPort } from 'esm-web-pro/types/networks';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import MapFiberConnection from 'esm-web-pro/containers/pages/network/objects/point12/MapFiberConnection';


type Props = {
    container: ODFType,
    cables: Array<Object>,
    port: ODFPort,
    ports: {[number]: {
        fiber: ?Object, port: ?Object,
    }},
    freeFibers: Object,
    editor: BaseEditor,
    map: Map,
    onClose: Function,
    onSave: Function,

    intl: IntlShape,
    connectFibersToPorts: Function,
};

const ConnectFibersODFPorts = (props: Props) => {
    const {
        container: ODF, cables, port, ports, freeFibers, onClose, onSave, editor, map: networkMap,
        intl: { formatMessage }, connectFibersToPorts,
    } = props;

    const [cable, setCable] = React.useState(null);
    const [fiber, setFiber] = React.useState(null);
    const [fiberUuid, setFiberUuid] = React.useState(null);
    const [range, setRange] = React.useState(1);
    const [rangeError, setRangeError] = React.useState(null);

    const prevCable = usePrevious(cable);
    const prevFiber = usePrevious(fiber);

    const getFiberUuid = React.useCallback(() => {
        if (!fiber || !cable) {
            setFiberUuid(null);
        } else if ((prevFiber !== fiber) || (prevCable !== cable)) {
            const { uuid } = cable.free_fibers.find((f) => f.sequence_number === Number(fiber));
            setFiberUuid(uuid);
        }
    }, [cable, fiber]);

    React.useEffect(() => {
        getFiberUuid();
    }, [getFiberUuid]);

    React.useEffect(() => {
        if (cable) {
            setFiber(Object.keys(freeFibers[cable.id])[0]);
        }
    }, [cable]);

    React.useEffect(() => (editor.unregisterAllLoadingCallback), []);

    const maxRange = React.useCallback(() => {
        let maxPort = port.sequence;
        Object.values(ports).forEach((odfPort) => {
            if (odfPort.sequence - maxPort === 1 && !odfPort.fiber) {
                maxPort = odfPort.sequence;
            }
        });

        return Math.min(maxPort - port.sequence + 1, freeFibers[Number(cable.id)][Number(fiber)]);
    }, [cable, fiber]);

    const handleSave = () => {
        connectFibersToPorts(ODF.uuid, {
            cable: cable.uuid,
            fiber,
            port: port.sequence,
            range,
        }).then(() => onSave());
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Box height="100%">
            <MapFiberConnection
                point={ODF.container}
                initialFiber={(port.port && port.port.fiber) ? port.port.fiber.uuid : null}
                secondFiber={fiberUuid}
                editor={editor}
                map={networkMap}
            />
            <Box
                direction="row"
                align="center"
                height="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex justify="between">
                    <Box flex={{ shrink: 0 }} pad={{ bottom: 'small', horizontal: 'small' }}>
                        <Box height="12px" flex={{ shrink: 0 }} />
                        <Box pad={{ horizontal: 'small' }}>
                            <CableWithFreeFibersSelect
                                cables={cables}
                                onChange={(uuid) => {
                                    setCable(cables.find((c) => c.uuid === uuid));
                                }}
                                value={cable ? cable.uuid : null}
                            />
                            <Box height="12px" flex={{ shrink: 0 }} />
                            {cable && (
                                <FiberSelect
                                    freeFibers={freeFibers}
                                    cable={cable}
                                    value={fiber}
                                    onChange={setFiber}
                                />
                            )}
                            <Box height="12px" flex={{ shrink: 0 }} />
                            {(cable && fiber) && (
                                <RangeSelect
                                    calculateMaxRange={maxRange}
                                    onChange={setRange}
                                    value={range}
                                    error={rangeError}
                                    onError={setRangeError}
                                />
                            )}
                        </Box>
                    </Box>
                    <ActionButtons
                        onClose={handleClose}
                        onSave={handleSave}
                        disabled={!cable || !fiber || !range || rangeError}
                    />
                </Box>
            </Box>
        </Box>
    );
};


const apiCallback = (apiClient: ApiClient) => ({
    connectFibersToPorts: {
        send: (uuid: string, data: Object) => apiClient.resources.odf.connectFibers(uuid, data),
    },
});

export default injectIntl(connectAPI(apiCallback)(ConnectFibersODFPorts));
